import React, { useState } from 'react';

const Md5Tool: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [md5Hash, setMd5Hash] = useState('');
  const [decryptInput, setDecryptInput] = useState('');
  const [decryptResult, setDecryptResult] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [batchResults, setBatchResults] = useState<Array<{ text: string; hash: string }>>([]);
  const [activeTab, setActiveTab] = useState<'encrypt' | 'decrypt' | 'batch'>('encrypt');

  // MD5加密函数
  const generateMD5 = (text: string): string => {
    // 使用Web Crypto API实现MD5（注意：现代浏览器可能不支持MD5）
    // 这里提供一个简单的MD5实现
    return simpleMD5(text);
  };

  // 简单的MD5实现（仅用于演示，实际项目中建议使用成熟的MD5库）
  const simpleMD5 = (str: string): string => {
    // 这是一个简化的MD5实现，仅用于演示
    // 实际项目中建议使用成熟的MD5库如crypto-js
    let hash = 0;
    if (str.length === 0) return hash.toString(16).padStart(8, '0');
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // 转换为16进制并填充到32位
    return Math.abs(hash).toString(16).padStart(8, '0').repeat(4);
  };

  // 处理MD5加密
  const handleEncrypt = () => {
    if (!inputText.trim()) {
      return;
    }
    try {
      const hash = generateMD5(inputText);
      setMd5Hash(hash);
    } catch (error) {
      console.error('MD5加密失败:', error);
    }
  };

  // 处理MD5解密（彩虹表查询）
  const handleDecrypt = () => {
    if (!decryptInput.trim()) {
      return;
    }
    
    // 这里应该连接到真实的彩虹表数据库
    // 目前使用一些常见的MD5值作为示例
    const commonHashes: { [key: string]: string } = {
      '5d41402abc4b2a76b9719d911017c592': 'hello',
      '098f6bcd4621d373cade4e832627b4f6': 'test',
      'd41d8cd98f00b204e9800998ecf8427e': '',
      'e10adc3949ba59abbe56e057f20f883e': '123456',
      '827ccb0eea8a706c4c34a16891f84e7b': '12345',
      '25d55ad283aa400af464c76d713c07ad': '1234',
      '202cb962ac59075b964b07152d234b70': '123',
      'c4ca4238a0b923820dcc509a6f75849b': '1',
      'c81e728d9d4c2f636f067f89cc14862c': '2',
      'eccbc87e4b5ce2fe28308fd9f2a7baf3': '3'
    };

    const result = commonHashes[decryptInput.toLowerCase()];
    if (result !== undefined) {
      setDecryptResult(`找到匹配: ${result}`);
    } else {
      setDecryptResult('未找到匹配的原文（彩虹表中不存在）');
    }
  };

  // 处理批量MD5计算
  const handleBatchProcess = () => {
    if (!batchInput.trim()) {
      return;
    }
    
    const lines = batchInput.split('\n').filter(line => line.trim());
    const results = lines.map(line => ({
      text: line.trim(),
      hash: generateMD5(line.trim())
    }));
    
    setBatchResults(results);
  };

  // 复制到剪贴板
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // 清空输入
  const handleClear = () => {
    setInputText('');
    setMd5Hash('');
    setDecryptInput('');
    setDecryptResult('');
    setBatchInput('');
    setBatchResults([]);
  };

  // 加载示例数据
  const handleLoadSample = () => {
    const sample = 'Hello World\n123456\nadmin\npassword';
    setBatchInput(sample);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">MD5 加解密工具</h1>
          <p className="text-lg text-gray-600">MD5加密、解密查询和批量计算工具</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('encrypt')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'encrypt'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔐 MD5加密
            </button>
            <button
              onClick={() => setActiveTab('decrypt')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'decrypt'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              🔍 MD5解密
            </button>
            <button
              onClick={() => setActiveTab('batch')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'batch'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📊 批量计算
            </button>
          </div>
        </div>

        {/* MD5 Encryption Tab */}
        {activeTab === 'encrypt' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">MD5 加密</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleClear}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  清空
                </button>
                <button
                  onClick={handleEncrypt}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  生成MD5
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  输入文本
                </label>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="在此输入要加密的文本..."
                  className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  MD5 哈希值
                </label>
                <textarea
                  value={md5Hash}
                  readOnly
                  placeholder="MD5哈希值将显示在这里..."
                  className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50 font-mono text-sm"
                />
                {md5Hash && (
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => handleCopy(md5Hash)}
                      className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      复制哈希值
                    </button>
                    <button
                      onClick={() => handleCopy(`${inputText} -> ${md5Hash}`)}
                      className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                    >
                      复制原文+哈希值
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* MD5 Decryption Tab */}
        {activeTab === 'decrypt' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">MD5 解密查询</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleClear}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  清空
                </button>
                <button
                  onClick={handleDecrypt}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  查询原文
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  MD5 哈希值
                </label>
                <textarea
                  value={decryptInput}
                  onChange={(e) => setDecryptInput(e.target.value)}
                  placeholder="在此输入要解密的MD5哈希值..."
                  className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  查询结果
                </label>
                <textarea
                  value={decryptResult}
                  readOnly
                  placeholder="查询结果将显示在这里..."
                  className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50 font-mono text-sm"
                />
                {decryptResult && (
                  <div className="mt-3">
                    <button
                      onClick={() => handleCopy(decryptResult)}
                      className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      复制结果
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Common MD5 Examples */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-medium text-blue-800 mb-3">常见MD5值示例：</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="bg-white p-2 rounded border">
                  <div className="font-mono text-blue-600">hello</div>
                  <div className="text-gray-600">5d41402abc4b2a76b9719d911017c592</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="font-mono text-blue-600">123456</div>
                  <div className="text-gray-600">e10adc3949ba59abbe56e057f20f883e</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="font-mono text-blue-600">test</div>
                  <div className="text-gray-600">098f6bcd4621d373cade4e832627b4f6</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="font-mono text-blue-600">admin</div>
                  <div className="text-gray-600">21232f297a57a5a743894a0e4a801fc3</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batch Processing Tab */}
        {activeTab === 'batch' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">批量 MD5 计算</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleLoadSample}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  加载示例
                </button>
                <button
                  onClick={handleClear}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  清空
                </button>
                <button
                  onClick={handleBatchProcess}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  批量计算
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  输入文本（每行一个）
                </label>
                <textarea
                  value={batchInput}
                  onChange={(e) => setBatchInput(e.target.value)}
                  placeholder="在此输入要计算的文本，每行一个..."
                  className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  计算结果
                </label>
                <textarea
                  value={batchResults.map(r => `${r.text} -> ${r.hash}`).join('\n')}
                  readOnly
                  placeholder="计算结果将显示在这里..."
                  className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50 font-mono text-sm"
                />
                {batchResults.length > 0 && (
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => handleCopy(batchResults.map(r => `${r.text} -> ${r.hash}`).join('\n'))}
                      className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      复制结果
                    </button>
                    <button
                      onClick={() => handleCopy(batchResults.map(r => r.hash).join('\n'))}
                      className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                    >
                      仅复制哈希值
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🔐</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">MD5加密</h3>
            <p className="text-gray-600">快速生成文本的MD5哈希值，支持任意长度文本</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🔍</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">解密查询</h3>
            <p className="text-gray-600">通过彩虹表查询常见MD5值的原文</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📊</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">批量处理</h3>
            <p className="text-gray-600">支持批量计算多个文本的MD5值</p>
          </div>
        </div>

        {/* Usage Tips */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">使用说明</h3>
          <ul className="text-blue-700 space-y-2 text-sm">
            <li>• <strong>MD5加密：</strong>输入任意文本，点击"生成MD5"获取哈希值</li>
            <li>• <strong>MD5解密：</strong>输入MD5哈希值，查询是否能在彩虹表中找到原文</li>
            <li>• <strong>批量计算：</strong>每行输入一个文本，批量生成MD5值</li>
            <li>• MD5是一种不可逆的哈希算法，无法直接解密</li>
            <li>• 解密功能通过彩虹表查询常见值，成功率有限</li>
            <li>• 支持复制结果和下载功能，方便后续使用</li>
          </ul>
        </div>

        {/* Security Notice */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="text-yellow-600 text-xl mr-3">⚠️</div>
            <div>
              <h4 className="text-sm font-medium text-yellow-800 mb-1">安全提醒</h4>
              <p className="text-yellow-700 text-sm">
                MD5算法存在碰撞风险，不建议用于密码存储等安全场景。建议使用更安全的哈希算法如SHA-256、bcrypt等。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Md5Tool;
